import { useEffect, useState } from 'react';
import { Badge, Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/bootstrap/Button';
import Popovers from '../../../components/bootstrap/Popovers';
import Icon from '../../../components/icon/Icon';
import {
	AddCircle,
	AdminPanelSettings,
	Assessment,
	Assignment,
	Campaign,
	Dashboard,
	DashboardCustomize,
	EditCalendar,
	FileUpload,
	FormatListBulleted,
	Group,
	Logout,
	ManageAccounts,
	Menu,
	MoreHoriz,
	Notifications,
	People,
	QueryStats,
	ReceiptLong,
	Sms,
	Support,
} from '../../../components/icon/material-icons';
import Header, { HeaderLeft, HeaderRight } from '../../../layout/Header/Header';
import {
	agencyPagesAppointment,
	agencyTemplateSms,
	messageUnreadPages,
	progressAgency,
	progressOperator,
	requestLeadPage,
} from '../../../menu';
import { useAgencyStore } from '../../../stores/useAgencyStore';
import { useNotificationsStore } from '../../../stores/useNotificationsStore';
import { useOperatorStore } from '../../../stores/useOperatorStore';
import { useUserStore } from '../../../stores/useUserStore';

const DefaultHeader = () => {
	const navigate = useNavigate();
	const logout = useUserStore((state) => state.logout);
	const [user] = useUserStore((state) => [state.user]);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [activeSection, setActiveSection] = useState('dashboard');

	const handleLogout = async () => {
		try {
			await logout();
			navigate('/login');
		} catch (error) {
			console.error('Errore durante il logout:', error);
		}
	};

	const [agency, getAgencyInformation] = useAgencyStore((state) => [
		state.agencyInformation,
		state.getAgencyInformation,
	]);

	const [operator, getOperatorInformation] = useOperatorStore((state) => [
		state.operatorInformation,
		state.getOperatorInformation,
	]);

	const [countNewNotifications, count] = useNotificationsStore((state) => [
		state.countNewNotifications,
		state.countNewNotification,
	]);

	useEffect(() => {
		if (user) countNewNotifications();
		if (user && user.role !== 'OPERATOR' && user?.uuidAgency) getAgencyInformation(user.uuidAgency);
		if (user?.uuidOperator) getOperatorInformation(user.uuidOperator);
	}, [user, countNewNotifications, getAgencyInformation, getOperatorInformation]);

	// Funzioni di navigazione
	const navigationFunctions = {
		goToLeads: () => {
			setActiveSection('leads');
			navigate(`../agency-lead`);
		},
		goToCollaboratorStats: () => {
			setActiveSection('stats');
			navigate(`../collaborator/stats`);
		},
		goToLeadsOperator: () => {
			setActiveSection('leads');
			navigate(`../homepage`);
		},
		goToPratiche: () => {
			setActiveSection('pratiche');
			navigate(`../pratiche`);
		},
		createLeads: () => {
			setActiveSection('leads');
			navigate(`../lead`);
		},
		goToProgress: () => {
			setActiveSection('stats');
			navigate(`../${progressOperator.progress.path}`);
		},
		goToReportAgency: () => {
			setActiveSection('stats');
			navigate(`../${progressAgency.progress.path}`);
		},
		goToSupport: () => {
			setActiveSection('support');
			navigate(`../support`);
		},
		goToNotification: () => navigate(`../${messageUnreadPages.message.path}`),
		goToRequestPage: () => {
			setActiveSection('leads');
			navigate(`../${requestLeadPage.requestLead.path}`);
		},
		goToAppointment: () => {
			setActiveSection('gestione');
			navigate(`../${agencyPagesAppointment.appointment.path}`);
		},
		goToEsiti: () => {
			setActiveSection('stats');
			navigate(`../esiti`);
		},
		goToTemplateSms: () => {
			setActiveSection('marketing');
			navigate(`../${agencyTemplateSms.templateSms.path}`);
		},
		goToSendSms: () => {
			setActiveSection('marketing');
			navigate(`../massive-sms`);
		},
		goToSettingsAgency: () => {
			setActiveSection('sistema');
			navigate(`../settings-agency`);
		},
		goChargeSms: () => navigate(`../payment`),
		goUploadFileLeads: () => {
			setActiveSection('leads');
			navigate(`../upload-file`);
		},
		goToFileUploaded: () => {
			setActiveSection('leads');
			navigate(`../file-uploaded`);
		},
		goToTickets: () => {
			setActiveSection('admin');
			navigate(`../admin-support`);
		},
		goToCollaborator: () => {
			setActiveSection('gestione');
			navigate(`../collaborator`);
		},
		goToHome: () => {
			setActiveSection('dashboard');
			navigate(`../home`);
		},
		goToStatsAgency: () => {
			setActiveSection('stats');
			navigate(`../stats-agency`);
		},
		goToAdvancedKenaStats: () => {
			setActiveSection('admin');
			navigate(`../advanced-kena-stats`);
		},
		goToListini: () => {
			setActiveSection('gestione');
			navigate(`../listini`);
		},
	};

	// Determina quali menu mostrare in base al ruolo
	const isAgency = user?.role === 'AGENCY';
	const isOperator = user?.role === 'OPERATOR';
	const isCollaborator = user?.role === 'COLLABORATOR';
	const isAdmin = user?.role === 'ADMIN';
	const isTeamLeader = user?.isTeamLeader;

	// Funzione per renderizzare badge di gruppo
	const renderGroupBadges = () => {
		if (isOperator && operator && operator.groups && operator?.groups?.length > 0) {
			return (
				<div className='group-badges d-flex align-items-center mx-2'>
					{operator.groups.map((group, index) => (
						<Badge
							key={group}
							bg={index % 2 === 0 ? 'success' : 'info'}
							className='mx-1 py-2 px-3 group-badge'
							style={{ fontSize: '0.85rem', borderRadius: '20px' }}>
							<Icon icon='LocalOffer' className='me-1' />
							{group}
						</Badge>
					))}
				</div>
			);
		}
		return null;
	};

	// Menu mobile per schermi piccoli
	const renderMobileMenu = () => {
		if (!mobileMenuOpen) return null;

		return (
			<div
				className='position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-75'
				style={{ zIndex: 1050 }}
				onClick={() => setMobileMenuOpen(false)}>
				<div
					className='position-absolute top-0 start-0 h-100 bg-white sidebar-menu p-0 overflow-auto'
					style={{ width: '280px', zIndex: 1051 }}
					onClick={(e) => e.stopPropagation()}>
					<div className='d-flex justify-content-between align-items-center p-3 sidebar-header'>
						<h5 className='m-0 fw-bold text-white'>Menu</h5>
						<Button
							color='light'
							className='btn-sm rounded-circle'
							onClick={() => setMobileMenuOpen(false)}>
							<Icon icon='Close' />
						</Button>
					</div>

					<div className='p-3'>
						{isAgency && (
							<>
								<div className='sidebar-section mb-5'>
									<h6 className='fw-bold mb-2 sidebar-title'>Dashboard</h6>
									<div className='d-grid gap-2'>
										<Button
											color={activeSection === 'dashboard' ? 'primary' : 'light'}
											className='sidebar-button'
											onClick={navigationFunctions.goToHome}>
											<Dashboard style={{ fontSize: '16px' }} />
											Dashboard
										</Button>
									</div>
								</div>

								<div className='sidebar-section'>
									<h6 className='fw-bold mb-2 sidebar-title'>Lead</h6>
									<div className='d-grid gap-2'>
										<Button
											color={activeSection === 'leads' ? 'primary' : 'light'}
											className='sidebar-button'
											onClick={navigationFunctions.goToLeads}>
											<FormatListBulleted style={{ fontSize: '16px' }} />I miei leads
										</Button>
										<Button
											color={activeSection === 'leads' ? 'primary' : 'light'}
											className='sidebar-button'
											onClick={navigationFunctions.createLeads}>
											<AddCircle style={{ fontSize: '16px' }} />
											Crea lead
										</Button>
										<Button
											color={activeSection === 'leads' ? 'primary' : 'light'}
											className='sidebar-button'
											onClick={navigationFunctions.goUploadFileLeads}>
											<FileUpload style={{ fontSize: '16px' }} />
											Carica file leads
										</Button>
										{!isTeamLeader && (
											<>
												<Button
													color={activeSection === 'leads' ? 'primary' : 'light'}
													className='sidebar-button'
													onClick={navigationFunctions.goToRequestPage}>
													<Assignment style={{ fontSize: '16px' }} />
													Richiedi leads
												</Button>
												<Button
													color={activeSection === 'leads' ? 'primary' : 'light'}
													className='sidebar-button'
													onClick={navigationFunctions.goToFileUploaded}>
													<FormatListBulleted style={{ fontSize: '16px' }} />
													File caricati
												</Button>
											</>
										)}
									</div>
								</div>

								<div className='sidebar-section'>
									<h6 className='fw-bold mb-2 sidebar-title'>Gestione</h6>
									<div className='d-grid gap-2'>
										<Button
											color={activeSection === 'gestione' ? 'primary' : 'light'}
											className='sidebar-button'
											onClick={navigationFunctions.goToAppointment}>
											<EditCalendar style={{ fontSize: '16px' }} />
											Appuntamenti
										</Button>
										<Button
											color={activeSection === 'gestione' ? 'primary' : 'light'}
											className='sidebar-button'
											onClick={navigationFunctions.goToPratiche}>
											<ManageAccounts style={{ fontSize: '16px' }} />
											Pratiche
										</Button>
										<Button
											color={activeSection === 'gestione' ? 'primary' : 'light'}
											className='sidebar-button'
											onClick={navigationFunctions.goToListini}>
											<ReceiptLong style={{ fontSize: '16px' }} />
											Listini
										</Button>
										<Button
											color={activeSection === 'gestione' ? 'primary' : 'light'}
											className='sidebar-button'
											onClick={navigationFunctions.goToCollaborator}>
											<Group style={{ fontSize: '16px' }} />
											Collaboratori
										</Button>
									</div>
								</div>

								<div className='sidebar-section'>
									<h6 className='fw-bold mb-2 sidebar-title'>Marketing</h6>
									<div className='d-grid gap-2'>
										<Button
											color={activeSection === 'marketing' ? 'info' : 'light'}
											className='sidebar-button'
											onClick={navigationFunctions.goToSendSms}>
											<Sms style={{ fontSize: '16px' }} />
											Invia SMS Massivo
										</Button>
										<Button
											color={activeSection === 'marketing' ? 'info' : 'light'}
											className='sidebar-button'
											onClick={navigationFunctions.goToTemplateSms}>
											<FormatListBulleted style={{ fontSize: '16px' }} />
											Template SMS
										</Button>
									</div>
								</div>

								{!isTeamLeader && (
									<>
										<div className='sidebar-section'>
											<h6 className='fw-bold mb-2 sidebar-title'>Reportistica</h6>
											<div className='d-grid gap-2'>
												<Button
													color={activeSection === 'stats' ? 'info' : 'light'}
													className='sidebar-button'
													onClick={navigationFunctions.goToEsiti}>
													<Assessment style={{ fontSize: '16px' }} />
													Esiti
												</Button>
												<Button
													color={activeSection === 'stats' ? 'info' : 'light'}
													className='sidebar-button'
													onClick={navigationFunctions.goToStatsAgency}>
													<QueryStats style={{ fontSize: '16px' }} />
													Statistiche
												</Button>
											</div>
										</div>

										<div className='sidebar-section'>
											<h6 className='fw-bold mb-2 sidebar-title'>Sistema</h6>
											<div className='d-grid gap-2'>
												<Button
													color={activeSection === 'sistema' ? 'secondary' : 'light'}
													className='sidebar-button'
													onClick={navigationFunctions.goToSettingsAgency}>
													<AdminPanelSettings style={{ fontSize: '16px' }} />
													Regole
												</Button>
												<Button
													color={activeSection === 'sistema' ? 'secondary' : 'light'}
													className='sidebar-button'
													onClick={navigationFunctions.goToSupport}>
													<Support style={{ fontSize: '16px' }} />
													Supporto
												</Button>
											</div>
										</div>
									</>
								)}
							</>
						)}

						{/* Menu per Operatori */}
						{isOperator && (
							<>
								<div className='sidebar-section'>
									<h6 className='fw-bold mb-2 sidebar-title'>Lead</h6>
									<div className='d-grid gap-2'>
										<Button
											color={activeSection === 'leads' ? 'primary' : 'light'}
											className='sidebar-button'
											onClick={navigationFunctions.goToLeadsOperator}>
											<FormatListBulleted style={{ fontSize: '16px' }} />I miei leads
										</Button>
										<Button
											color={activeSection === 'leads' ? 'success' : 'light'}
											className='sidebar-button'
											onClick={navigationFunctions.createLeads}>
											<AddCircle style={{ fontSize: '16px' }} />
											Crea leads
										</Button>
									</div>
								</div>

								<div className='sidebar-section'>
									<h6 className='fw-bold mb-2 sidebar-title'>Reportistica</h6>
									<div className='d-grid gap-2'>
										<Button
											color={activeSection === 'stats' ? 'info' : 'light'}
											className='sidebar-button'
											onClick={navigationFunctions.goToProgress}>
											<Assessment style={{ fontSize: '16px' }} />
											Report
										</Button>
										<Button
											color={activeSection === 'stats' ? 'info' : 'light'}
											className='sidebar-button'
											onClick={navigationFunctions.goToCollaboratorStats}>
											<QueryStats style={{ fontSize: '16px' }} />
											Statistiche
										</Button>
									</div>
								</div>
							</>
						)}

						{/* Menu per Collaboratori */}
						{isCollaborator && (
							<>
								<div className='sidebar-section'>
									<h6 className='fw-bold mb-2 sidebar-title'>Lead</h6>
									<div className='d-grid gap-2'>
										<Button
											color={activeSection === 'leads' ? 'primary' : 'light'}
											className='sidebar-button'
											onClick={navigationFunctions.goToLeads}>
											<People style={{ fontSize: '16px' }} />
											Leads
										</Button>
										<Button
											color={activeSection === 'leads' ? 'success' : 'light'}
											className='sidebar-button'
											onClick={navigationFunctions.createLeads}>
											<AddCircle style={{ fontSize: '16px' }} />
											Crea leads
										</Button>
									</div>
								</div>

								<div className='sidebar-section'>
									<h6 className='fw-bold mb-2 sidebar-title'>Gestione</h6>
									<div className='d-grid gap-2'>
										<Button
											color={activeSection === 'gestione' ? 'primary' : 'light'}
											className='sidebar-button'
											onClick={navigationFunctions.goToAppointment}>
											<EditCalendar style={{ fontSize: '16px' }} />
											Appuntamenti
										</Button>
										<Button
											color={activeSection === 'gestione' ? 'primary' : 'light'}
											className='sidebar-button'
											onClick={navigationFunctions.goToPratiche}>
											<ManageAccounts style={{ fontSize: '16px' }} />
											Pratiche
										</Button>
									</div>
								</div>

								<div className='sidebar-section'>
									<h6 className='fw-bold mb-2 sidebar-title'>Reportistica</h6>
									<div className='d-grid gap-2'>
										<Button
											color={activeSection === 'stats' ? 'info' : 'light'}
											className='sidebar-button'
											onClick={navigationFunctions.goToCollaboratorStats}>
											<QueryStats style={{ fontSize: '16px' }} />
											Statistiche
										</Button>
									</div>
								</div>
							</>
						)}

						{/* Menu per Admin */}
						{isAdmin && (
							<>
								<div className='sidebar-section'>
									<h6 className='fw-bold mb-2 sidebar-title'>Amministrazione</h6>
									<div className='d-grid gap-2'>
										<Button
											color={activeSection === 'admin' ? 'primary' : 'light'}
											className='sidebar-button'
											onClick={navigationFunctions.goToTickets}>
											<Support style={{ fontSize: '16px' }} />
											Ticket
										</Button>
										{user.username === 'turismodentaleadmin' && (
											<Button
												color={activeSection === 'admin' ? 'danger' : 'light'}
												className='sidebar-button'
												onClick={navigationFunctions.goToAdvancedKenaStats}>
												<DashboardCustomize style={{ fontSize: '16px' }} />
												Statistiche avanzate
											</Button>
										)}
									</div>
								</div>
							</>
						)}
					</div>
				</div>
			</div>
		);
	};

	return (
		<>
			<Header>
				<HeaderLeft className='d-flex flex-wrap align-items-center'>
					{/* Logo o nome app */}
					<div className='app-logo me-3 d-none d-md-flex'>
						<img src={require('../../../assets/img/logo_transparent.png')} alt='Logo' className='login-logo' style={{ height: '60px', width: 'auto' }} />
					</div>

					{/* Pulsante per menu mobile su schermi piccoli */}
					<Button
						color='light'
						onClick={() => setMobileMenuOpen(true)}
						className='d-xl-none me-2 menu-toggle'>
						<Menu style={{ fontSize: '24px' }} />
					</Button>

					<div className='d-none d-xl-flex flex-wrap align-items-center'>
						{/* Primo gruppo: Dashboard e Lead */}
						<div className='menu-group d-flex me-2'>
							{/* AGENCY - Dashboard Button */}
							{isAgency && (
								<Popovers trigger='hover' desc='Dashboard' className='me-3 mb-2 mb-md-2'>
									<Button
										color={activeSection === 'dashboard' ? 'primary' : 'light'}
										onClick={navigationFunctions.goToHome}
										className='modern-button'>
										<Dashboard style={{ fontSize: '16px' }} />
										<span>Dashboard</span>
									</Button>
								</Popovers>
							)}

							{/* AGENCY - Menu Lead */}
							<div className='d-flex align-items-center ms-3'>
								{isAgency && (
									<Dropdown className='me-3 mb-2 mb-md-0'>
										<Dropdown.Toggle
											variant={activeSection === 'leads' ? 'primary' : 'light'}
											className='modern-button'>
											<People style={{ fontSize: '16px' }} />
											<span>Lead</span>
										</Dropdown.Toggle>
										<Dropdown.Menu className='modern-dropdown'>
											<Dropdown.Item
												onClick={navigationFunctions.goToLeads}
												className='dropdown-item-modern'>
												<FormatListBulleted style={{ fontSize: '16px' }} />I miei leads
											</Dropdown.Item>
											<Dropdown.Item
												onClick={navigationFunctions.createLeads}
												className='dropdown-item-modern'>
												<AddCircle style={{ fontSize: '16px' }} />
												Crea lead
											</Dropdown.Item>
											<Dropdown.Item
												onClick={navigationFunctions.goUploadFileLeads}
												className='dropdown-item-modern'>
												<FileUpload style={{ fontSize: '16px' }} />
												Carica file leads
											</Dropdown.Item>
											{!isTeamLeader && (
												<>
													<Dropdown.Item
														onClick={navigationFunctions.goToRequestPage}
														className='dropdown-item-modern'>
														<Assignment style={{ fontSize: '16px' }} />
														Richiedi leads
													</Dropdown.Item>
													<Dropdown.Item
														onClick={navigationFunctions.goToFileUploaded}
														className='dropdown-item-modern'>
														<FormatListBulleted style={{ fontSize: '16px' }} />
														File caricati
													</Dropdown.Item>
												</>
											)}
										</Dropdown.Menu>
									</Dropdown>
								)}
							</div>
						</div>

						{/* Separatore visivo */}
						{isAgency && <div className='menu-divider d-none d-lg-block'></div>}

						{/* Secondo gruppo: Gestione e altre funzionalità */}
						<div className='menu-group d-flex me-2'>
							{/* COLLABORATOR - Lead Button */}
							{isCollaborator && (
								<>
									<Dropdown className='me-3 mb-2 mb-md-0'>
										<Dropdown.Toggle
											variant={activeSection === 'leads' ? 'primary' : 'light'}
											className='modern-button'>
											<People style={{ fontSize: '16px' }} />
											<span>Lead</span>
										</Dropdown.Toggle>
										<Dropdown.Menu className='modern-dropdown'>
											<Dropdown.Item
												onClick={navigationFunctions.goToLeads}
												className='dropdown-item-modern'>
												<FormatListBulleted style={{ fontSize: '16px' }} />I miei leads
											</Dropdown.Item>
											<Dropdown.Item
												onClick={navigationFunctions.createLeads}
												className='dropdown-item-modern'>
												<AddCircle style={{ fontSize: '16px' }} />
												Crea lead
											</Dropdown.Item>
										</Dropdown.Menu>
									</Dropdown>
								</>
							)}

							{/* OPERATOR/COLLABORATOR - Stats Button */}
							{isCollaborator && (
								<Popovers trigger='hover' desc='Statistiche' className='me-3 mb-2 mb-md-0'>
									<Button
										color={activeSection === 'stats' ? 'primary' : 'light'}
										onClick={navigationFunctions.goToCollaboratorStats}
										className='modern-button'>
										<QueryStats style={{ fontSize: '16px' }} />
										<span>Stats</span>
									</Button>
								</Popovers>
							)}

							{/* AGENCY/COLLABORATOR - Gestione dropdown */}
							{(isAgency || isCollaborator) && (
								<Dropdown className='me-3 mb-2 mb-md-0'>
									<Dropdown.Toggle
										variant={activeSection === 'gestione' ? 'primary' : 'light'}
										className='modern-button'>
										<ManageAccounts style={{ fontSize: '16px' }} />
										<span>Gestione</span>
									</Dropdown.Toggle>
									<Dropdown.Menu className='modern-dropdown'>
										<Dropdown.Item
											onClick={navigationFunctions.goToAppointment}
											className='dropdown-item-modern'>
											<EditCalendar style={{ fontSize: '16px' }} />
											Appuntamenti
										</Dropdown.Item>
										<Dropdown.Item
											onClick={navigationFunctions.goToPratiche}
											className='dropdown-item-modern'>
											<ManageAccounts style={{ fontSize: '16px' }} />
											Pratiche
										</Dropdown.Item>
										{isAgency && (
											<>
												{isAgency && user.username === 'kenadmin' && (
													<Dropdown.Item
														onClick={navigationFunctions.goToListini}
														className='dropdown-item-modern'>
														<ReceiptLong style={{ fontSize: '16px' }} />
														Listini
													</Dropdown.Item>
												)}
												<Dropdown.Item
													onClick={navigationFunctions.goToCollaborator}
													className='dropdown-item-modern'>
													<Group style={{ fontSize: '16px' }} />
													Collaboratori
												</Dropdown.Item>
											</>
										)}
									</Dropdown.Menu>
								</Dropdown>
							)}

							{/* AGENCY/COLLABORATOR - Marketing dropdown */}
							{(isAgency || isCollaborator) && (
								<Dropdown className='me-3 mb-2 mb-md-0'>
									<Dropdown.Toggle
										variant={activeSection === 'marketing' ? 'info' : 'light'}
										className='modern-button'>
										<Campaign style={{ fontSize: '16px' }} />
										<span>Marketing</span>
									</Dropdown.Toggle>
									<Dropdown.Menu className='modern-dropdown'>
										<Dropdown.Item
											onClick={navigationFunctions.goToSendSms}
											className='dropdown-item-modern'>
											<Sms style={{ fontSize: '16px' }} />
											Invia SMS Massivo
										</Dropdown.Item>
										<Dropdown.Item
											onClick={navigationFunctions.goToTemplateSms}
											className='dropdown-item-modern'>
											<FormatListBulleted style={{ fontSize: '16px' }} />
											Template SMS
										</Dropdown.Item>
									</Dropdown.Menu>
								</Dropdown>
							)}

							{/* AGENCY - Reportistica dropdown (solo se non TeamLeader) */}
							{isAgency && !isTeamLeader && (
								<Dropdown className='me-3 mb-2 mb-md-0'>
									<Dropdown.Toggle
										variant={activeSection === 'stats' ? 'info' : 'light'}
										className='modern-button'>
										<Assessment style={{ fontSize: '16px' }} />
										<span>Report</span>
									</Dropdown.Toggle>
									<Dropdown.Menu className='modern-dropdown'>
										<Dropdown.Item
											onClick={navigationFunctions.goToEsiti}
											className='dropdown-item-modern'>
											<Assessment style={{ fontSize: '16px' }} />
											Esiti
										</Dropdown.Item>
										<Dropdown.Item
											onClick={navigationFunctions.goToStatsAgency}
											className='dropdown-item-modern'>
											<QueryStats style={{ fontSize: '16px' }} />
											Statistiche
										</Dropdown.Item>
									</Dropdown.Menu>
								</Dropdown>
							)}

							{/* OPERATOR - Crea lead & I miei lead */}
							{isOperator && (
								<>
									<Button
										color={activeSection === 'leads' ? 'success' : 'light'}
										onClick={navigationFunctions.createLeads}
										className='me-3 mb-2 mb-md-0 modern-button'>
										<AddCircle style={{ fontSize: '16px' }} />
										<span>Crea leads</span>
									</Button>
									<Button
										color={activeSection === 'leads' ? 'primary' : 'light'}
										onClick={navigationFunctions.goToLeadsOperator}
										className='me-3 mb-2 mb-md-0 modern-button'>
										<FormatListBulleted style={{ fontSize: '16px' }} />
										<span>I miei leads</span>
									</Button>
									<Button
										color={activeSection === 'stats' ? 'info' : 'light'}
										onClick={navigationFunctions.goToProgress}
										className='me-3 mb-2 mb-md-0 modern-button'>
										<Assessment style={{ fontSize: '16px' }} />
										<span>Report</span>
									</Button>
								</>
							)}

							{/* AGENCY - Impostazioni ed Extra (solo se non TeamLeader e non collaboratore) */}
							{isAgency && !isTeamLeader && !isCollaborator && (
								<Dropdown className='me-3 mb-2 mb-md-0'>
									<Dropdown.Toggle
										variant={activeSection === 'sistema' ? 'secondary' : 'light'}
										className='modern-button'>
										<MoreHoriz style={{ fontSize: '16px' }} />
										<span>Sistema</span>
									</Dropdown.Toggle>
									<Dropdown.Menu className='modern-dropdown'>
										<Dropdown.Item
											onClick={navigationFunctions.goToSettingsAgency}
											className='dropdown-item-modern'>
											<AdminPanelSettings style={{ fontSize: '16px' }} />
											Regole
										</Dropdown.Item>
										<Dropdown.Item
											onClick={navigationFunctions.goToSupport}
											className='dropdown-item-modern'>
											<Support style={{ fontSize: '16px' }} />
											Supporto
										</Dropdown.Item>
									</Dropdown.Menu>
								</Dropdown>
							)}
						</div>

						{renderGroupBadges()}
					</div>
				</HeaderLeft>

				<HeaderRight className='d-flex align-items-center'>
					{/* ADMIN - Statistiche Avanzate & Ticket */}
					{isAdmin && user.username === 'turismodentaleadmin' && (
						<Button
							color='danger'
							onClick={navigationFunctions.goToAdvancedKenaStats}
							className='modern-button me-2'>
							<DashboardCustomize style={{ fontSize: '16px' }} />
							<span className='d-none d-md-inline'>Statistiche avanzate</span>
						</Button>
					)}

					{isAdmin && !user.isClient && (
						<Button
							color='primary'
							onClick={navigationFunctions.goToTickets}
							className='modern-button me-2'>
							<Support style={{ fontSize: '16px' }} />
							<span className='d-none d-md-inline'>Ticket</span>
						</Button>
					)}

					{/* AGENCY - Crediti */}
					{isAgency && (
						<div className='d-none d-sm-flex me-3'>
							<Popovers trigger='hover' desc='Crediti disponibili'>
								<div className='credit-badge d-flex align-items-center p-2 rounded'>
									<Icon icon='MonetizationOn' className='me-2 text-success' />
									<span className='fw-bold'>{agency?.credits || 0}</span>
								</div>
							</Popovers>

							<Popovers trigger='hover' desc='Crediti SMS disponibili'>
								<div className='credit-badge d-flex align-items-center p-2 rounded'>
									<Icon icon='Phone' className='me-2 text-info' />
									<span className='fw-bold'>{agency?.creditiSms?.toFixed(2) || 0}</span>
								</div>
							</Popovers>
						</div>
					)}

					{/* Informazioni utente */}
					<div className='user-info d-none d-sm-flex align-items-center rounded p-2 me-3'>
						<Icon icon='Person' style={{ fontSize: '16px' }} className='me-2 text-primary' />
						<span className='fw-bold'>
							{isOperator ? operator?.username?.toUpperCase() : user?.username?.toUpperCase()}
						</span>
					</div>

					{/* Notifiche */}
					{(isAgency || isAdmin || isOperator) && !user.isClient && (
						<Popovers trigger='hover' desc='Notifiche'>
							<Button
								color='light'
								onClick={navigationFunctions.goToNotification}
								className='modern-button position-relative'>
								<Notifications style={{ fontSize: '16px' }} />
								{count > 0 && (
									<Badge
										bg='danger'
										className='notification-badge position-absolute top-0 start-100 translate-middle rounded-circle'>
										{count}
									</Badge>
								)}
							</Button>
						</Popovers>
					)}

					{/* Pulsante logout */}
					<Button color='danger' onClick={handleLogout} className='modern-button logout-button'>
						<Logout style={{ fontSize: '16px' }} />
						<span className='d-none d-sm-inline'>Logout</span>
					</Button>
				</HeaderRight>
			</Header>

			{/* Render del menu mobile */}
			{renderMobileMenu()}

			{/* Stili CSS per la responsività */}
			<style>{`
			/* Header moderno */
			.modern-header {
				background-color: #ffffff;
				box-shadow: 0 2px 10px rgba(0,0,0,0.08);
				border-radius: 0;
				padding: 0.75rem 1.25rem;
				margin-bottom: 1.5rem;
			}
			
			/* Logo dell'app */
			.app-logo {
				padding-left: 0;
				padding-right: 0;
				margin-right: 1rem;
			}
			
			/* Pulsanti moderni */
			.modern-button {
				border-radius: 8px;
				padding: 0.5rem 1rem;
				transition: all 0.2s ease;
				display: flex;
				align-items: center;
				gap: 8px;
				border: none;
				font-weight: 500;
				box-shadow: 0 1px 3px rgba(0,0,0,0.1);
			}
			
			.modern-button:hover {
				transform: translateY(-2px);
				box-shadow: 0 4px 6px rgba(0,0,0,0.1);
			}
			
			/* Stile per i dropdown */
			.modern-dropdown {
					border-radius: 12px;
					box-shadow: 0 6px 16px rgba(0,0,0,0.1);
					border: none;
					padding: 0.75rem 0;
					min-width: 200px;
					margin-top: 8px;
			}
			
			.dropdown-item-modern {
				display: flex;
				align-items: center;
				gap: 10px;
				padding: 0.65rem 1.25rem;
				border-radius: 8px;
				margin: 0.2rem 0.5rem;
				transition: all 0.2s;
				font-weight: 400;
			}
			
			.dropdown-item-modern:hover {
				background-color: #f8f9fa;
				transform: translateX(3px);
			}
			
			/* Badge di notifica */
			.notification-badge {
				padding: 0.25rem 0.5rem;
				font-size: 0.75rem;
			}
			
			/* Divisori di menu */
			.menu-divider {
				height: 28px;
				width: 1px;
				background-color: #e9ecef;
				margin: 0 1rem;
			}
			
			/* Pulsante di menu per mobile */
			.menu-toggle {
				border-radius: 8px;
				width: 40px;
				height: 40px;
				display: flex;
				align-items: center;
				justify-content: center;
				padding: 0;
			}
			
			/* Informazioni utente */
			.user-info {
				background-color: #f8f9fa;
				border-radius: 8px;
				transition: all 0.2s;
			}
			
			.user-info:hover {
				background-color: #e9ecef;
			}
			
			/* Badge di credito */
			.credit-badge {
				background-color: #f8f9fa;
				margin-right: 0.75rem;
				border-radius: 8px;
				transition: all 0.2s;
			}
			
			.credit-badge:hover {
				background-color: #e9ecef;
				transform: translateY(-2px);
			}
			
			/* Stile per i badge di gruppo */
			.group-badge {
				transition: all 0.2s;
				box-shadow: 0 2px 5px rgba(0,0,0,0.08);
			}
			
			.group-badge:hover {
				transform: translateY(-2px);
				box-shadow: 0 4px 8px rgba(0,0,0,0.12);
			}
			
			/* Stili per il menu laterale mobile */
			.sidebar-menu {
				box-shadow: 0 0 20px rgba(0,0,0,0.15);
				transition: all 0.3s ease;
			}
			
			.sidebar-header {
				background: linear-gradient(135deg, #4a6cf7 0%, #24379f 100%);
				margin-bottom: 1rem;
			}
			
			.sidebar-title {
				color: #4a6cf7;
				border-bottom: 1px solid #e9ecef;
				padding-bottom: 0.5rem;
				margin-bottom: 1rem;
			}
			
			.sidebar-section {
				margin-bottom: 1.5rem;
			}
			
			.sidebar-button {
				display: flex;
				align-items: center;
				gap: 10px;
				text-align: left;
				padding: 0.65rem 1rem;
				border-radius: 8px;
				transition: all 0.2s;
				margin-bottom: 0.5rem;
				border: none;
			}
			
			.sidebar-button:hover {
				transform: translateX(3px);
			}
			
			/* Bottone di logout */
			.logout-button {
				background-color: #ff5e5e;
				border: none;
			}
			
			.logout-button:hover {
				background-color: #ff3b3b;
			}
			
			/* Animazioni */
			@keyframes fadeIn {
				from { opacity: 0; transform: translateY(-10px); }
				to { opacity: 1; transform: translateY(0); }
			}
			
			/* Responsiveness */
			@media (max-width: 1199.98px) {
				.header-actions-buttons {
					display: none;
				}
			}
			
			@media (max-width: 767.98px) {
				.modern-button {
					padding: 0.4rem 0.8rem;
					font-size: 0.9rem;
				}
				
				.credit-badge, .user-info {
					padding: 0.3rem 0.6rem;
					font-size: 0.8rem;
				}
			}
		`}</style>
		</>
	);
};

export default DefaultHeader;
